import { useContext, useMemo, useCallback, useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { t } from '@lingui/core/macro'
import { object } from 'prop-types'
import { CvsAutoComplete, CvsTextField, CvsFieldMapper } from '@components'
import { connectorContext } from '@contexts'
import { styled as transientStyled } from '@utils/styleHelpers'
import { api } from 'api'
import { css } from '@emotion/react'
import { templateCopyContext } from '../context'

const FullWidth = styled.div`
  width: 100%;
`

const FullWidthTextField = transientStyled(CvsTextField)(
  () => css`
    width: 100%;
  `,
)

export const AdobeInbound = ({ assignment }) => {
  const { template, onChange } = useContext(templateCopyContext)
  const { byId } = useContext(connectorContext)
  const [realms, setRealms] = useState([])
  const [rawReports, setRawReports] = useState([])
  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState({ realms: false, reports: false })

  useEffect(() => {
    if (assignment.connector_id) {
      setLoading(old => ({ ...old, reports: true, realms: true }))
      api.post(`/api/v1/account/connector/${assignment.connector_id}/realm/`).then(({ data }) => {
        setRealms(data.realms.map(r => ({ label: r.realm_name, value: r.remote_realm_id })))
        setLoading(old => ({ ...old, realms: false }))
      })
    }
  }, [assignment.connector_id])

  useEffect(() => {
    if (assignment?.metadata?.realmLinked) {
      setLoading(old => ({ ...old, reports: true }))
      api
        .get(
          `/api/v1/account/connector/${assignment.connector_id}/realm/${assignment.metadata.realmLinked}/target/list`,
        )
        .then(({ data }) => {
          setLoading(old => ({ ...old, reports: false }))
          setReports(data.targets.map(report => ({ label: report.name, value: report.id })))
          setRawReports(data.targets)
        })
    }
  }, [assignment.metadata.realmLinked])

  const fieldOptions = useMemo(
    () =>
      Object.values(template.templateFieldsById).map(field => ({
        label: field.field?.name,
        value: field.field?.id?.toString(),
      })),

    [template],
  )

  const connectors = useMemo(
    () =>
      Object.values(byId)
        .filter(
          connector =>
            connector.connector_type_id ===
            (assignment.connector_type_id
              ? assignment.connector_type_id
              : byId[assignment.connector_id].connector_type_id),
        )
        .map(({ name, id }) => ({
          label: name,
          value: id,
        })),
    [assignment, byId],
  )

  const onAdobeInboundChange = useCallback(
    (value, name) => {
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...old[assignment.id],
          [name]: value,
        },
      }))
    },
    [assignment, onChange],
  )

  const onAdobeInboundMetadataChange = useCallback(
    (value, name) =>
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...old[assignment.id],
          metadata: {
            ...old[assignment.id].metadata,
            [name]: value,
          },
        },
      })),
    [onChange, assignment],
  )

  const values = useMemo(() => {
    const {
      adAccountsLinked: _adAccountsLinked,
      realmLinked: _realmLinked,
      reportLinked: _reportLinked,
      ...rest
    } = template.integrationsById[assignment.id].metadata
    return rest
  }, [template, assignment])

  const fromOptions = useMemo(() => {
    const alreadySelected = Object.keys(assignment.metadata).filter(
      opt => opt !== 'adAccountsLinked' && opt !== 'realmLinked' && opt !== 'reportLinked',
    )

    const classifications = [
      'Key',
      ...(rawReports
        .find(r => r.id === assignment.metadata.reportLinked)
        ?.classifications_names.filter(r => !alreadySelected.includes(r)) || []),
    ]

    return [
      ...alreadySelected.map(o => ({ label: o, value: o })),
      ...classifications.map(c => ({ label: c, value: c })),
    ]
  }, [template, reports, assignment])

  return (
    <FullWidth>
      <FullWidthTextField
        label={t`Integration name`}
        onChange={e => onAdobeInboundChange(e.target.value, 'name')}
        value={assignment.name}
      />
      <CvsAutoComplete
        options={connectors}
        onSelected={selected => onAdobeInboundChange(selected, 'connector_id')}
        selected={assignment.connector_id}
        label={t`Connected Adobe account`}
      />
      <CvsAutoComplete
        options={realms}
        onSelected={selected => onAdobeInboundMetadataChange(selected, 'realmLinked')}
        selected={assignment.metadata.realmLinked}
        label={t`Report suite`}
        showSpinner={loading.realms}
      />
      <CvsAutoComplete
        options={reports}
        onSelected={selected => {
          const { adAccountsLinked, realmLinked, reportLinked } = assignment.metadata
          onChange(old => ({
            ...old,
            [assignment.id]: {
              ...old[assignment.id],
              metadata: { adAccountsLinked, realmLinked, reportLinked },
            },
          }))
          onAdobeInboundMetadataChange(selected, 'reportLinked')
        }}
        selected={assignment.metadata.reportLinked}
        label={t`Report`}
        showSpinner={loading.reports}
      />
      <CvsFieldMapper
        fromOptions={fromOptions}
        toOptions={fieldOptions}
        requiredMappings={[]}
        value={values}
        onSelect={value => {
          // TODO: on first save it needs "fieldMapping" apparently but not after that. Probably transform it before it gets here.
          const { adAccountsLinked, realmLinked, reportLinked } = assignment.metadata
          const replacement = { adAccountsLinked, realmLinked, reportLinked }
          Object.keys(value).forEach(key => {
            replacement[fromOptions.find(opt => opt.value === key).label] = value[key]
          })
          onChange(old => ({
            ...old,
            [assignment.id]: {
              ...old[assignment.id],
              metadata: replacement,
            },
          }))
        }}
      />
    </FullWidth>
  )
}

AdobeInbound.propTypes = {
  assignment: object.isRequired,
}
